// @flow
import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { cpf as CPF } from 'cpf-cnpj-validator';
import { connect } from 'react-redux';
// Components
import {
  FormButton,
  PageLoader,
  validationHelpers,
  PhoneInput,
  sanitizePhoneNumber,
  FormControlInput,
} from '../../../common';
import MyAccountAddressesPage from '../../containers/MyAccountAddressesPage';
import MyAccountPasswordPage from '../../containers/MyAccountPasswordPage';
// Styles
import './MyAccountInformation.css';
// assets
import rotate_icon from '../../images/rotate-icon.svg';
// Helpers
import { useDeepCompareEffect } from '../../../common';
import MyAccountPayment from './MyAccountPayment';
import { deleteCreditCard, getCreditCards } from '../../actions';
import {
  creditCardsSelector,
  allowedPaymentMethodsSelector,
  deletingdUserCreditCardLoadingSelector,
  gettingUserCreditCardsLoadingSelector,
  userIdSelector,
} from '../../selectors';

type Props = {
  information: {
    firstName: string,
    lastName: string,
    cpf: string,
    email: string,
    phone: string,
  },
  loading: boolean,
  editingLoading: boolean,
  serverErrors: any,
  onSubmitUpdate: (
    firstName: string,
    lastName: string,
    cpf: string,
    email: string,
    phone: string,
  ) => void,
  userId: ?number,
  creditCards: any,
  creditCardsLoading: boolean,
  deletingLoading: boolean,
  allowedPaymentMethods: any,
};

const MyAccountInformation = (props: Props) => {
  const [accountForm, setAccountForm] = useState({
    firstName: props.information ? props.information.firstName : '',
    lastName: props.information ? props.information.lastName : '',
    cpf: props.information ? props.information.cpf : '',
    email: props.information ? props.information.email : '',
    phone: props.information ? props.information.phone : '',
  });
  const [formErrors, setFormErrors] = useState({
    firstNameError: '',
    lastNameError: '',
    cpfError: '',
    emailError: '',
    phoneError: '',
  });
  const [showServerErrors, setShowServerErrors] = useState(false);
  const [isPasswordPopupOpen, setPasswordPopupOpen] = useState(false);

  const openPasswordPopup = () => {
    setPasswordPopupOpen(true);
  };

  const closePasswordPopup = () => {
    setPasswordPopupOpen(false);
  };

  useEffect(() => {
    if (props.userId) {
      props.getCreditCards();
    }
  }, [props.userId]);

  useDeepCompareEffect(() => {
    // If the user information has changed we should update our accountForm
    if (!_.isEqual(props.information, accountForm)) {
      setAccountForm({
        firstName: props.information ? props.information.firstName : '',
        lastName: props.information ? props.information.lastName : '',
        cpf: props.information ? props.information.cpf : '',
        email: props.information ? props.information.email : '',
        phone: props.information ? props.information.phone : '',
      });
    }
    // eslint-disable-next-line
  }, [props.information]);

  // Validates the form on submission. If there are no validation error dispatches action.
  const validateUserInput = () => {
    let noValidationErrors = true;

    const firstNameError = validationHelpers.user.firstName(
      accountForm.firstName,
    );
    setFormErrors(prev => ({ ...prev, firstNameError }));
    if (firstNameError) noValidationErrors = false;

    const lastNameError = validationHelpers.user.lastName(accountForm.lastName);
    setFormErrors(prev => ({ ...prev, lastNameError }));
    if (lastNameError) noValidationErrors = false;

    const emailError = validationHelpers.user.email(accountForm.email);
    setFormErrors(prev => ({ ...prev, emailError }));
    if (emailError) noValidationErrors = false;

    const phoneError = validationHelpers.phone(accountForm.phone);
    setFormErrors(prev => ({ ...prev, phoneError }));
    if (phoneError) noValidationErrors = false;

    if (noValidationErrors) {
      setShowServerErrors(true);
      props.onSubmitUpdate(
        accountForm.firstName,
        accountForm.lastName,
        accountForm.cpf,
        accountForm.email,
        sanitizePhoneNumber(accountForm.phone),
      );
    } else {
      console.error('Validation error on user editing form.', formErrors);
    }
  };

  const fieldValidators = {
    firstName: () => validationHelpers.user.firstName(accountForm.firstName),
    lastName: () => validationHelpers.user.lastName(accountForm.lastName),
    email: () => validationHelpers.user.email(accountForm.email),
  };

  const validateField = (field: string) =>
    setFormErrors(prev => ({
      ...prev,
      [`${field}Error`]: fieldValidators[field](),
    }));

  const handleFormChange = (target: string, value: string) => {
    if (accountForm[target] != value) setShowServerErrors(false);
    setAccountForm(prev => ({ ...prev, [target]: value }));
    if (accountForm[target] != value)
      setFormErrors(prev => ({ ...prev, [`${target}Error`]: '' }));
  };

  const backendErrors =
    showServerErrors && !props.editingLoading
      ? (props.serverErrors && props.serverErrors.errors) || {}
      : {};
  const backendError = (...fields: string[]) => {
    const key = fields.find(field => backendErrors[field]);
    return key ? [].concat(backendErrors[key])[0] : '';
  };

  const updateSucceeded =
    showServerErrors &&
    !props.editingLoading &&
    _.isEmpty(backendErrors) &&
    _.isEmpty(props.serverErrors);

  if (props.loading) {
    return <PageLoader />;
  }

  return (
    <div>
      {isPasswordPopupOpen && (
        <div className="my-account-information-password-popup-overlay">
          <div className="my-account-information-password-popup">
            <MyAccountPasswordPage onClose={closePasswordPopup} />
          </div>
        </div>
      )}
      <div className="my-account-information-main-container">
        <div className="my-account-information">
          <div className="my-account-information-header-container">
            <div className="my-account-information-header-text-content">
              <h3>Meu Perfil</h3>
              <h6>Gerenciar e proteger sua conta</h6>
            </div>
            <button
              className="my-account-information-change-password-button"
              onClick={openPasswordPopup}
            >
              <img src={rotate_icon} alt="Icone de Troca"></img>
              Trocar senha
            </button>
          </div>
          <hr></hr>
          <form
            onSubmit={e => {
              e.preventDefault();
              validateUserInput();
            }}
          >
            <div className="my-account-information-content">
              <div className="my-account-information-content-row">
                <FormControlInput
                  variant="rounded"
                  label="Nome"
                  value={accountForm.firstName}
                  handleChangeText={value =>
                    handleFormChange('firstName', value)
                  }
                  handleBlur={() => validateField('firstName')}
                  handleFocus={value => handleFormChange('firstName', value)}
                  errorMessage={
                    formErrors.firstNameError ||
                    backendError('first_name', 'firstName')
                  }
                />

                <FormControlInput
                  variant="rounded"
                  label="Sobrenome"
                  value={accountForm.lastName}
                  handleChangeText={value =>
                    handleFormChange('lastName', value)
                  }
                  handleBlur={() => validateField('lastName')}
                  handleFocus={value => handleFormChange('lastName', value)}
                  errorMessage={
                    formErrors.lastNameError ||
                    backendError('last_name', 'lastName')
                  }
                />

                <FormControlInput
                  variant="rounded"
                  className="my-account-information-medium-input"
                  label="CPF"
                  value={`***.***.${CPF.format(accountForm.cpf).substring(
                    8,
                    14,
                  )}`}
                  errorMessage={formErrors.cpfError || ''}
                  readonly
                />
              </div>
              <div className="my-account-information-content-row">
                <FormControlInput
                  type="text"
                  variant="rounded"
                  className="my-account-information-medium-input"
                  label="Email"
                  value={accountForm.email}
                  handleChangeText={value => handleFormChange('email', value)}
                  handleBlur={() => validateField('email')}
                  handleFocus={value => handleFormChange('email', value)}
                  errorMessage={formErrors.emailError || backendError('email')}
                />
                <PhoneInput
                  value={accountForm.phone}
                  onChange={value => handleFormChange('phone', value)}
                  errorMessage={formErrors.phoneError}
                  serverErrors={backendErrors}
                />
              </div>
            </div>
            {backendError('base') && (
              <p className="my-account-information-server-error">
                {backendError('base')}
              </p>
            )}
            {updateSucceeded && (
              <p className="my-account-information-success-message">
                Dados atualizados com sucesso!
              </p>
            )}
            <div className="my-account-information-submit-content">
              <div className="my-account-information-submit-button">
                <FormButton
                  text="Salvar"
                  icon=""
                  buttonType={'form-button-quarternary'}
                  loading={props.editingLoading}
                />
              </div>
            </div>
          </form>
        </div>
        <MyAccountAddressesPage />
        <MyAccountPayment
          creditCards={props.creditCards}
          allowedPaymentMethods={props.allowedPaymentMethods}
          loading={props.creditCardsLoading}
          deleteCreditCard={props.deleteCreditCard}
          deletingLoading={props.deletingLoading}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    userId: userIdSelector(state),
    creditCards: creditCardsSelector(state),
    creditCardsLoading: gettingUserCreditCardsLoadingSelector(state),
    deletingLoading: deletingdUserCreditCardLoadingSelector(state),
    allowedPaymentMethods: allowedPaymentMethodsSelector(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps) => {
  return {
    getCreditCards: () => {
      dispatch(getCreditCards());
    },
    deleteCreditCard: (creditCardId: number) => {
      dispatch(deleteCreditCard(creditCardId));
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(MyAccountInformation);
